/**
 * Orders' search provider for the Z1 search slot (O6; docs/audits/ROLLOUT-ORDERS-SHELL.md §2.7,
 * §3 D9), on the Quotes provider's model (quotes/quoteSearchProvider.jsx). /api/search returns no
 * orders, so without a provider the slot never finds one; an order number is what staff type.
 *
 * Orders come from the O1a list contract, GET /api/orders?view=all&q=<term>&limit=5 (newest
 * first, the contract's default sort), so an order is found exactly as the list finds it (number,
 * customer, quote number). The slot asks only after its debounce and from 2 characters, so this
 * is one extra request per settled term (§7.3), never more. A row shows the number, the customer,
 * the status chip's label and tone (orderStatusBadge: a legacy `completed` reads Delivered) and
 * the total; it opens /orders/:id (push: list → record).
 *
 * afterGroup: the slot shows this group after its own Customers group, and this seeAll after
 * "See all in Customers" (SearchSlot.jsx), so the dropdown reads Quotes, …, Customers, Orders.
 */
import { Package } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { orderStatusBadge } from './orderStatusBadge';
import { formatCentsCurrency } from '../../utils/quoteFormat';

export const SLOT_ORDER_LIMIT = 5;

export function slotOrdersUrl(term) {
  const sp = new URLSearchParams({ view: 'all', q: String(term || '').trim(), limit: String(SLOT_ORDER_LIMIT) });
  return `/api/orders?${sp}`;
}

export const orderSearchProvider = Object.freeze({
  id: 'orders',
  label: 'Orders',
  kind: 'order',
  afterGroup: 'customer',
  errorText: 'Orders search unavailable',
  async search(term) {
    const res = await apiClient.get(slotOrdersUrl(term));
    const orders = res?.data?.orders;
    return Array.isArray(orders) ? orders.slice(0, SLOT_ORDER_LIMIT) : [];
  },
  rowKey: (o) => `order-${o.id}`,
  href: (o) => `/orders/${o.id}`,
  renderRow(o) {
    const status = orderStatusBadge(o.status);
    return (
      <>
        <Package className="shell-search-icon" aria-hidden="true" />
        <span className="shell-search-main">
          <span className="shell-search-title">{o.order_number || `Order ${o.id}`}</span>
          <span className="shell-search-sub">{o.customer?.name || 'No customer'}</span>
        </span>
        <span className="shell-search-status" style={{ '--shell-search-tone': `var(--status-${status.tone})` }}>
          <span className="shell-search-dot" aria-hidden="true" />
          {status.label}
        </span>
        <span className="shell-search-value">{formatCentsCurrency(o.total_cents)}</span>
      </>
    );
  },
  seeAll: Object.freeze({
    title: 'See all in Orders',
    sub: (term) => `“${term}” in the orders list`,
    href: (term) => `/orders?view=all&q=${encodeURIComponent(term)}`
  })
});
